import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Archive, ArchiveRestore, Mail, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const ORDER_STATUS_LABELS = {
  nuovo: "Nuovo", in_attesa_pagamento: "In attesa di pagamento", pagato: "Pagato",
  in_preparazione: "In preparazione", ordinato_al_fornitore: "Ordinato al fornitore",
  in_attesa_disponibilita: "In attesa di disponibilità", spedito: "Spedito",
  consegnato: "Consegnato", completato: "Completato", annullato: "Annullato", rimborsato: "Rimborsato",
};
const NEEDS_CONFIRM = ["annullato", "rimborsato"];

export function OrdersPanel({ orders, reload }) {
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("attivi"); // attivi | archiviati | tutti

  const toggleArchive = async () => {
    const target = !sel.archived;
    if (target && !window.confirm(`Archiviare l'ordine ${sel.order_number || ""}?\n\nSoft archive: resta nel database, nello storico e nelle statistiche; esce solo dalla lista attiva.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${sel.session_id}/${target ? "archive" : "unarchive"}`);
      toast.success(target ? "Ordine archiviato (dati e storico preservati)" : "Ordine ripristinato");
      setSel((s) => ({ ...s, archived: target }));
      reload();
    } catch { toast.error("Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  const open = (o) => setSel(o) || setForm({
    order_status: o.order_status || "nuovo", note: "",
    courier: o.courier || "", tracking: o.tracking || "", expected_date: o.expected_date || "",
  });

  const save = async () => {
    if (NEEDS_CONFIRM.includes(form.order_status) && form.order_status !== (sel.order_status || "nuovo")) {
      if (!window.confirm(`Confermi il passaggio a "${ORDER_STATUS_LABELS[form.order_status]}"? Lo stato del pagamento NON viene modificato.`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${sel.session_id}`, {
        order_status: form.order_status, note: form.note,
        courier: form.courier, tracking: form.tracking, expected_date: form.expected_date,
      });
      toast.success("Ordine aggiornato");
      setSel((s) => ({ ...s, ...data })); setForm({ ...form, note: "" }); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento"); }
    finally { setBusy(false); }
  };

  const notify = async () => {
    if (!window.confirm("Inviare un'email di aggiornamento al cliente con lo stato attuale?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${sel.session_id}/notify`);
      toast.success("Email di aggiornamento inviata al cliente");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  if (sel && form) {
    const stLabel = ORDER_STATUS_LABELS[sel.order_status || "nuovo"];
    return (
      <div className="space-y-6" data-testid="order-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => setSel(null)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="order-back">
            <ArrowLeft size={14} /> Tutti gli ordini
          </button>
          <span className="text-[10px] tracking-[0.14em] uppercase border border-gold/50 text-gold px-3 py-1.5" data-testid="order-status-badge">{stLabel}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Ordine{sel.order_number ? ` ${sel.order_number}` : ""}</div>
            <p className="text-sm text-taupe">Rif. <span className="font-mono text-xs">{sel.session_id?.slice(0, 18)}…</span> · {fmtDate(sel.created_at)}</p>
            <p className="text-sm mt-1">{sel.customer_email || "—"}</p>
            <p className="text-sm mt-1">Pagamento: <strong>{sel.payment_status === "paid" ? "pagato" : sel.payment_status || "—"}</strong> <span className="text-xs text-taupe">(stato del provider, non modificabile qui)</span></p>
            {(sel.items || []).map((i, k) => (
              <div key={k} className="flex justify-between text-sm py-1.5 border-b border-hair/60 last:border-0 mt-1">
                <span>{i.quantity}× {i.name || i.product_name}</span>
                <span className="text-taupe">{eur((i.price || 0) * (i.quantity || 1))}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 font-medium">
              <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Totale</span>
              <span>{eur(sel.amount)}</span>
            </div>
          </div>

          <div className="bg-white border border-hair p-5 md:p-6 space-y-4" data-testid="order-form">
            <div className="eyebrow">Stato e spedizione</div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stato ordine</Label>
              <Select value={form.order_status} onValueChange={(v) => setForm({ ...form, order_status: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ORDER_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Corriere</Label>
                <Input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} className="rounded-none mt-1" data-testid="order-courier" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Tracking</Label>
                <Input value={form.tracking} onChange={(e) => setForm({ ...form, tracking: e.target.value })} className="rounded-none mt-1" data-testid="order-tracking" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Data prevista consegna</Label>
              <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="rounded-none mt-1" data-testid="order-expected" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nota interna (registrata nello storico)</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-none mt-1" data-testid="order-note" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={save} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="order-save"><Save size={14} /> Salva</button>
              <button onClick={notify} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="order-notify"><Mail size={14} /> Comunica al cliente</button>
              <button onClick={toggleArchive} disabled={busy} className={`${btn} border border-hair text-taupe hover:border-ink hover:text-ink`} data-testid="order-archive">
                {sel.archived ? <><ArchiveRestore size={14} /> Ripristina</> : <><Archive size={14} /> Archivia</>}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6" data-testid="order-history">
          <div className="eyebrow mb-3">Storico stati</div>
          {(sel.order_history || []).length === 0 && <p className="text-xs text-taupe">Nessun cambio di stato registrato.</p>}
          {(sel.order_history || []).slice().reverse().map((h, i) => (
            <div key={i} className="text-xs text-taupe py-2 border-b border-hair/60 last:border-0">
              <p>{fmtDate(h.at)} — {ORDER_STATUS_LABELS[h.from] || h.from} → <strong className="text-ink">{ORDER_STATUS_LABELS[h.to] || h.to}</strong> · {h.by}</p>
              {h.note && <p className="pl-3 mt-1 italic">{h.note}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="orders-list">
      <div className="flex gap-2 flex-wrap">
        {[["attivi", "Attivi"], ["archiviati", "Archiviati"], ["tutti", "Tutti"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`min-h-[44px] px-4 text-[11px] tracking-[0.12em] uppercase border transition-colors ${view === v ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}
            data-testid={`orders-view-${v}`}>{l}</button>
        ))}
      </div>
      {(() => {
        const shown = orders.filter((o) => view === "tutti" || (view === "archiviati" ? o.archived : !o.archived));
        return shown.length === 0 ? <div className="p-12 text-center text-taupe bg-white border border-hair">Nessun ordine{view === "archiviati" ? " archiviato" : ""}</div> : shown.map((o) => (
        <button key={o.session_id} onClick={() => open(o)} className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors" data-testid={`order-row-${o.session_id}`}>
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {o.order_number && <span className="font-medium text-sm" data-testid={`order-number-${o.session_id}`}>{o.order_number}</span>}
                <span className="font-serif text-lg">{eur(o.amount)}</span>
                <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${o.payment_status === "paid" ? "text-emerald-700 border-emerald-700/40" : "text-amber-700 border-amber-700/40"}`}>
                  {o.payment_status === "paid" ? "Pagato" : "Non pagato"}
                </span>
                <span className="text-[10px] tracking-[0.14em] uppercase border border-gold/50 text-gold px-2 py-0.5">
                  {ORDER_STATUS_LABELS[o.order_status || "nuovo"]}
                </span>
                {o.archived && <span className="text-[10px] tracking-[0.14em] uppercase border border-hair text-taupe px-2 py-0.5">Archiviato</span>}
              </div>
              <p className="text-sm text-taupe mt-1 truncate">{o.customer_email || "—"} · {(o.items || []).map((i) => `${i.quantity}× ${i.name || i.product_name}`).join(" · ")}</p>
            </div>
            <p className="text-xs text-taupe shrink-0">{fmtDate(o.created_at)}</p>
          </div>
        </button>
      )); })()}
    </div>
  );
}
